import { Analytics } from '@cobliteam/analytics';
import type { SessionUser } from '@cobliteam/common-types';
import { Location } from '@cobliteam/common-types';
import DropdownMenu from '@cobliteam/ui/components/DropdownMenu';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';

interface UserButtonProps {
  user: SessionUser;
  logout: () => void;
}

export default function UserButton({ user, logout }: UserButtonProps) {
  const [open, setOpen] = useState(false);
  const history = useHistory();

  const goToClientArea = () => {
    Analytics.track('UserButton - Navigate - ClientArea');
    history.push(Location.BUSINESS_MANAGER_HOME);
  };

  const goToFinance = () => {
    Analytics.track('UserButton - Navigate - Finance');
    history.push(Location.BUSINESS_MANAGER_FINANCEIRO);
  };

  const handleLogout = () => {
    Analytics.track('UserButton - Click - Logout');
    logout();
  };

  const goToMyAccount = () => {
    Analytics.track('UserButton - Navigate - MyAccount');
    history.push(Location.CONFIG);
  };

  const goToTrainings = () => {
    Analytics.track('UserButton - Navigate - Trainings');
    history.push(Location.TRAINING);
  };

  const goToSettings = () => {
    Analytics.track('UserButton - Navigate - Settings');
    history.push(Location.CONFIG);
  };

  const goToContact = () => {
    Analytics.track('UserButton - Navigate - Contact');
    window.open('https://suporte.cobli.co/contato-cobli', '_blank');
  };

  const goToCRM = () => {
    Analytics.track('UserButton - Navigate - Orders');
    history.push(Location.CRM_SCHEDULE);
  };

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(isOpen) => {
        if (isOpen) {
          Analytics.track('UserButton - Open');
        }
        setOpen(isOpen);
      }}
    >
      <DropdownMenu.Trigger asChild>
        <div
          data-testid='header-user-button'
          className={cn(
            'flex cursor-pointer items-center gap-1 rounded-full border border-gray-4 px-1.5 py-1 transition-colors duration-100',
            'data-[state=open]:border-gray-6 data-[state=open]:bg-gray-2'
          )}
        >
          <div className='flex size-8 items-center justify-center rounded-full bg-gray-4'>
            <Icon name='user' size='md' />
          </div>
          <Icon className='transition-transform duration-100' name={open ? 'chevron-up' : 'chevron-down'} />
        </div>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content className='max-w-64'>
        <DropdownMenu.Label className='flex min-w-0 flex-col items-start gap-0 px-2 pt-1'>
          <span className='truncate font-medium text-gray-12 text-sm'>{user.name}</span>
          <span className='truncate font-normal text-gray-10 text-xs'>{user.email}</span>
        </DropdownMenu.Label>
        <DropdownMenu.Separator />
        <DropdownMenu.Group>
          <DropdownMenu.Item onClick={goToTrainings}>
            <DropdownMenu.Icon icon={<Icon name='graduation-cap' />} />
            <DropdownMenu.Label>Treinamentos</DropdownMenu.Label>
          </DropdownMenu.Item>

          <DropdownMenu.Item onClick={goToContact}>
            <DropdownMenu.Icon icon={<Icon name='headset' />} />
            <DropdownMenu.Label>Contato</DropdownMenu.Label>
          </DropdownMenu.Item>
        </DropdownMenu.Group>
        <DropdownMenu.Separator />
        <DropdownMenu.Group>
          <DropdownMenu.Item onClick={goToFinance}>
            <DropdownMenu.Icon icon={<Icon name='credit-card' />} />
            <DropdownMenu.Label>Financeiro</DropdownMenu.Label>
          </DropdownMenu.Item>
          <DropdownMenu.Item onClick={goToCRM}>
            <DropdownMenu.Icon icon={<Icon name='shopping-cart' />} />
            <DropdownMenu.Label>Pedidos</DropdownMenu.Label>
          </DropdownMenu.Item>
          <DropdownMenu.Item onClick={goToClientArea}>
            <DropdownMenu.Icon icon={<Icon name='building' />} />
            <DropdownMenu.Label>Área do cliente</DropdownMenu.Label>
          </DropdownMenu.Item>
        </DropdownMenu.Group>
        <DropdownMenu.Separator />
        <DropdownMenu.Item onClick={goToMyAccount}>
          <DropdownMenu.Icon icon={<Icon name='user' />} />
          <DropdownMenu.Label>Minha conta</DropdownMenu.Label>
        </DropdownMenu.Item>
        <DropdownMenu.Item onClick={goToSettings}>
          <DropdownMenu.Icon icon={<Icon name='settings' />} />
          <DropdownMenu.Label>Configurações gerais</DropdownMenu.Label>
        </DropdownMenu.Item>
        <DropdownMenu.Separator />
        <DropdownMenu.Item onClick={handleLogout}>
          <DropdownMenu.Icon icon={<Icon name='log-out' />} />
          <DropdownMenu.Label>Sair</DropdownMenu.Label>
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu>
  );
}
